import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_CONFIG, FORM_CATEGORIES, OPS_ROLES, fmtDuration } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChevronLeft, ChevronRight, PlusCircle, CalendarDays, MapPin, Clock, Timer } from "lucide-react";
import { toast } from "sonner";

const VIEWS = ["month", "week", "day"];
const TERMINAL = ["arrived", "completed", "cancelled"];

function EventChip({ job, onClick, compact, draggable, onDragStart }) {
  const cat = CATEGORY_CONFIG[job.category] || { color: "#48484a", tint: "#eaeaea", label: job.category };
  const dur = job.category === "delivery" ? fmtDuration(job.estimated_duration_min) : null;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      draggable={draggable}
      onDragStart={draggable ? (e) => onDragStart(e, job.id) : undefined}
      data-testid={`cal-event-${job.id}`}
      className={`flex w-full items-center gap-1 truncate rounded-md px-1.5 py-1 text-left text-[11px] font-semibold transition-transform hover:scale-[1.02] ${draggable ? "cursor-grab active:cursor-grabbing" : ""}`}
      style={{ backgroundColor: cat.tint, color: cat.color, borderLeft: `3px solid ${cat.color}` }}
      title={`${dayjs(job.scheduled_at).format("h:mm A")} · ${job.customer_name} (${cat.label})${dur ? ` · ${dur} on site` : ""}${draggable ? " — drag to reschedule" : ""}`}
    >
      <span className="opacity-80">{dayjs(job.scheduled_at).format("h:mm")}</span>
      <span className="truncate text-stark-ink">{job.customer_name}</span>
      {dur && <span className="ml-auto flex shrink-0 items-center gap-0.5 opacity-80"><Timer className="h-2.5 w-2.5" />{dur}</span>}
      {!dur && !compact && job.assigned_tech_names?.[0] && <span className="ml-auto truncate opacity-70">{job.assigned_tech_names[0].split(" ")[0]}</span>}
    </button>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [techs, setTechs] = useState([]);
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(dayjs());
  const [fCat, setFCat] = useState("all");
  const [fTech, setFTech] = useState("all");
  const [fAm, setFAm] = useState("all");
  const [fTeam, setFTeam] = useState("all");
  const [dragId, setDragId] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);

  useEffect(() => {
    api.get("/deliveries").then(({ data }) => setJobs(data)).catch(() => setJobs([]));
    api.get("/users", { params: { role: "driver" } }).then(({ data }) => setTechs(data)).catch(() => {});
  }, []);

  const isOps = OPS_ROLES.includes(user.role);
  // Only admin & generic ops accounts can see both delivery + installation; scoped roles already see one.
  const canSeeBoth = user.role === "admin" || user.role === "ops";

  const ams = useMemo(() => {
    const s = new Set();
    (jobs || []).forEach((j) => { if (j.am_name) s.add(j.am_name); });
    return [...s].sort();
  }, [jobs]);

  const filtered = useMemo(() => (jobs || []).filter((j) => {
    if (fTeam !== "all" && (CATEGORY_CONFIG[j.category]?.team || "installation") !== fTeam) return false;
    if (fCat !== "all" && j.category !== fCat) return false;
    if (fTech !== "all" && !(j.assigned_tech_ids || []).includes(fTech)) return false;
    if (fAm !== "all" && j.am_name !== fAm) return false;
    return true;
  }), [jobs, fTeam, fCat, fTech, fAm]);

  const byDay = useMemo(() => {
    const m = {};
    filtered.forEach((j) => {
      if (!j.scheduled_at) return;
      const k = dayjs(j.scheduled_at).format("YYYY-MM-DD");
      (m[k] = m[k] || []).push(j);
    });
    Object.values(m).forEach((arr) => arr.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)));
    return m;
  }, [filtered]);

  const step = (dir) => setCursor((c) => c.add(dir, view));
  const open = (j) => navigate(`/jobs/${j.id}`);

  const canDrag = (j) => isOps && !TERMINAL.includes(j.status);
  const onDragStart = (e, jobId) => { e.dataTransfer.setData("text/plain", jobId); e.dataTransfer.effectAllowed = "move"; setDragId(jobId); };
  const allowDrop = (e, key) => { if (!isOps) return; e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (key !== dragOverKey) setDragOverKey(key); };
  const rescheduleTo = async (jobId, targetDay) => {
    const job = (jobs || []).find((j) => j.id === jobId);
    if (!job) return;
    const orig = dayjs(job.scheduled_at);
    if (orig.isSame(targetDay, "day")) return;
    const newAt = targetDay.hour(orig.hour()).minute(orig.minute()).second(0);
    try {
      const { data } = await api.post(`/deliveries/${jobId}/reschedule`, {
        scheduled_at: newAt.format("YYYY-MM-DDTHH:mm:ss"),
        window_minutes: job.window_minutes || 60,
        reason: "Rescheduled via calendar drag",
      });
      setJobs((prev) => prev.map((j) => (j.id === jobId ? data : j)));
      toast.success(`Moved ${job.customer_name} to ${targetDay.format("MMM D")}${job.customer_email ? " — client notified" : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't reschedule");
    }
  };
  const onDropDay = (e, day) => {
    e.preventDefault();
    const id = e.dataTransfer.getData("text/plain") || dragId;
    setDragOverKey(null);
    setDragId(null);
    if (id) rescheduleTo(id, day);
  };

  const title = view === "month" ? cursor.format("MMMM YYYY")
    : view === "week" ? `${cursor.startOf("week").format("MMM D")} – ${cursor.endOf("week").format("MMM D, YYYY")}`
    : cursor.format("dddd, MMM D, YYYY");

  // Month matrix
  const monthCells = useMemo(() => {
    const start = cursor.startOf("month").startOf("week");
    return Array.from({ length: 42 }, (_, i) => start.add(i, "day"));
  }, [cursor]);
  const weekCells = useMemo(() => {
    const start = cursor.startOf("week");
    return Array.from({ length: 7 }, (_, i) => start.add(i, "day"));
  }, [cursor]);

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-semibold text-stark-terracotta">Hello, {user.name.split(" ")[0]}</p>
          <h1 className="mt-1 flex items-center gap-2 text-3xl font-extrabold tracking-tight text-stark-ink sm:text-4xl">
            <CalendarDays className="h-8 w-8 text-stark-terracotta" /> Schedule
          </h1>
        </div>
        {isOps && (
          <Button data-testid="dashboard-new-job-btn" onClick={() => navigate("/jobs/new")} className="h-11 rounded-xl bg-stark-terracotta font-bold text-white hover:bg-[#bd4f2c]">
            <PlusCircle className="mr-1.5 h-4 w-4" /> New Job
          </Button>
        )}
      </div>

      {/* Toolbar */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1 rounded-xl bg-white p-1 shadow-sm">
          <button onClick={() => step(-1)} className="rounded-lg p-2 hover:bg-stark-tint" data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></button>
          <button onClick={() => setCursor(dayjs())} className="rounded-lg px-3 py-1.5 text-sm font-semibold hover:bg-stark-tint" data-testid="cal-today">Today</button>
          <button onClick={() => step(1)} className="rounded-lg p-2 hover:bg-stark-tint" data-testid="cal-next"><ChevronRight className="h-4 w-4" /></button>
        </div>
        <div className="text-lg font-bold text-stark-ink" data-testid="cal-title">{title}</div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canSeeBoth && (
            <div className="flex rounded-xl bg-white p-1 shadow-sm" data-testid="cal-team-toggle">
              {[["all", "All"], ["delivery", "Delivery"], ["installation", "Installation"]].map(([v, label]) => (
                <button key={v} onClick={() => { setFTeam(v); setFCat("all"); }} data-testid={`cal-team-${v}`}
                  className={`rounded-lg px-3 py-1.5 text-sm font-semibold transition-colors ${fTeam === v ? "bg-stark-terracotta text-white" : "text-stark-muted hover:bg-stark-tint"}`}>{label}</button>
              ))}
            </div>
          )}
          <div className="flex rounded-xl bg-white p-1 shadow-sm">
            {VIEWS.map((v) => (
              <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`}
                className={`rounded-lg px-3 py-1.5 text-sm font-semibold capitalize transition-colors ${view === v ? "bg-stark-ink text-white" : "text-stark-muted hover:bg-stark-tint"}`}>{v}</button>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Select value={fCat} onValueChange={setFCat}>
          <SelectTrigger className="h-9 w-40 bg-white" data-testid="cal-filter-category"><SelectValue placeholder="Job type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All job types</SelectItem>{FORM_CATEGORIES.map((k) => <SelectItem key={k} value={k}>{CATEGORY_CONFIG[k].label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fTech} onValueChange={setFTech}>
          <SelectTrigger className="h-9 w-40 bg-white" data-testid="cal-filter-tech"><SelectValue placeholder="Crew" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All crew</SelectItem>{techs.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fAm} onValueChange={setFAm}>
          <SelectTrigger className="h-9 w-44 bg-white" data-testid="cal-filter-am"><SelectValue placeholder="Account Manager" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All account mgrs</SelectItem>{ams.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
        </Select>
        <div className="ml-1 flex flex-wrap items-center gap-2">
          {FORM_CATEGORIES.map((k) => (
            <span key={k} className="flex items-center gap-1 text-[11px] font-semibold text-stark-muted">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: CATEGORY_CONFIG[k].color }} /> {CATEGORY_CONFIG[k].label}
            </span>
          ))}
        </div>
      </div>

      {!jobs ? (
        <Skeleton className="mt-6 h-[560px] rounded-2xl" />
      ) : (
        <div className="stark-card mt-4 overflow-hidden p-0" data-testid="calendar">
          {view === "month" && (
            <>
              <div className="grid grid-cols-7 border-b border-stark-border bg-stark-surface/60 text-center text-xs font-bold uppercase tracking-wide text-stark-muted">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d} className="py-2">{d}</div>)}
              </div>
              <div className="grid grid-cols-7">
                {monthCells.map((day) => {
                  const key = day.format("YYYY-MM-DD");
                  const evs = byDay[key] || [];
                  const inMonth = day.month() === cursor.month();
                  const isToday = day.isSame(dayjs(), "day");
                  return (
                    <div key={key} onClick={() => { setCursor(day); setView("day"); }}
                      onDragOver={(e) => allowDrop(e, key)}
                      onDragLeave={() => setDragOverKey((k) => (k === key ? null : k))}
                      onDrop={(e) => onDropDay(e, day)}
                      className={`min-h-[104px] cursor-pointer border-b border-r border-stark-border p-1.5 transition-colors hover:bg-stark-tint/40 ${inMonth ? "" : "bg-stark-surface/40"} ${dragOverKey === key ? "ring-2 ring-inset ring-stark-terracotta bg-stark-tint/60" : ""}`}
                      data-testid={`cal-day-${key}`}>
                      <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${isToday ? "bg-stark-terracotta text-white" : inMonth ? "text-stark-ink" : "text-stark-border-strong"}`}>{day.date()}</div>
                      <div className="space-y-1">
                        {evs.slice(0, 3).map((j) => <EventChip key={j.id} job={j} onClick={() => open(j)} compact draggable={canDrag(j)} onDragStart={onDragStart} />)}
                        {evs.length > 3 && <div className="pl-1 text-[11px] font-semibold text-stark-muted">+{evs.length - 3} more</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {view === "week" && (
            <div className="grid grid-cols-7">
              {weekCells.map((day) => {
                const key = day.format("YYYY-MM-DD");
                const evs = byDay[key] || [];
                const isToday = day.isSame(dayjs(), "day");
                return (
                  <div key={key}
                    onDragOver={(e) => allowDrop(e, key)}
                    onDragLeave={() => setDragOverKey((k) => (k === key ? null : k))}
                    onDrop={(e) => onDropDay(e, day)}
                    data-testid={`cal-day-${key}`}
                    className={`min-h-[420px] border-r border-stark-border p-2 last:border-r-0 transition-colors ${dragOverKey === key ? "bg-stark-tint/60 ring-2 ring-inset ring-stark-terracotta" : ""}`}>
                    <div className={`mb-2 text-center text-xs font-bold ${isToday ? "text-stark-terracotta" : "text-stark-muted"}`}>
                      {day.format("ddd")} <span className={`ml-1 ${isToday ? "rounded-full bg-stark-terracotta px-1.5 text-white" : "text-stark-ink"}`}>{day.date()}</span>
                    </div>
                    <div className="space-y-1.5">
                      {evs.length === 0 && <div className="pt-4 text-center text-[11px] text-stark-border-strong">—</div>}
                      {evs.map((j) => <EventChip key={j.id} job={j} onClick={() => open(j)} draggable={canDrag(j)} onDragStart={onDragStart} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {view === "day" && (
            <div className="p-4">
              {(byDay[cursor.format("YYYY-MM-DD")] || []).length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <CalendarDays className="h-10 w-10 text-stark-border-strong" />
                  <p className="mt-3 font-semibold text-stark-ink">Nothing scheduled</p>
                  <p className="text-sm text-stark-muted">No jobs on {cursor.format("MMM D")} for these filters.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {(byDay[cursor.format("YYYY-MM-DD")] || []).map((j) => {
                    const cat = CATEGORY_CONFIG[j.category] || { color: "#48484a", tint: "#eaeaea", label: j.category };
                    return (
                      <button key={j.id} onClick={() => open(j)} data-testid={`cal-event-${j.id}`}
                        className="flex w-full items-center gap-4 rounded-xl border border-stark-border p-4 text-left transition-all hover:-translate-y-0.5 hover:shadow-md"
                        style={{ borderLeft: `4px solid ${cat.color}` }}>
                        <div className="w-20 shrink-0 text-sm font-bold text-stark-ink"><Clock className="mr-1 inline h-3.5 w-3.5" />{dayjs(j.scheduled_at).format("h:mm A")}</div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="rounded px-1.5 py-0.5 text-[10px] font-bold uppercase text-white" style={{ backgroundColor: cat.color }}>{cat.label}</span>
                            <span className="truncate font-bold text-stark-ink">{j.customer_name}</span>
                            {j.category === "delivery" && fmtDuration(j.estimated_duration_min) && (
                              <span className="flex shrink-0 items-center gap-1 rounded-full bg-stark-tint px-2 py-0.5 text-[11px] font-bold text-stark-terracotta"><Timer className="h-3 w-3" />{fmtDuration(j.estimated_duration_min)} on site</span>
                            )}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-stark-muted"><MapPin className="h-3 w-3" />{j.address}</div>
                        </div>
                        {j.assigned_tech_names?.[0] && <div className="hidden text-xs font-semibold text-stark-muted sm:block">{j.assigned_tech_names[0]}</div>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
